/**
 * ReactRenderHelper
 * -----------------
 * React 18 removed the legacy `ReactDOM.render(element, container)` and
 * `ReactDOM.unmountComponentAtNode(container)` APIs. Calling them now logs
 * "ReactDOM.render is no longer supported in React 18" and silently runs the
 * whole app in React-17 compatibility mode (no automatic batching, no
 * concurrent features).
 *
 * This app mounts dozens of independent "mini apps" / modals imperatively and
 * re-renders or unmounts them by container element. React 18's createRoot API
 * requires keeping one root per container (calling createRoot twice on the same
 * node warns), so this helper maintains a registry of roots keyed by container.
 *
 * It re-exports every member of `react-dom` (createPortal, flushSync, etc.) and
 * only overrides `render` / `unmountComponentAtNode`, so it is a drop-in
 * replacement for the old `import ReactDOM from 'react-dom'` default import:
 *
 *     import { ReactRenderHelper as ReactDOM } from '../../../Scripts/Util/ReactRenderHelper';
 *     ReactDOM.render(<App />, document.getElementById('root'));
 *     ReactDOM.unmountComponentAtNode(document.getElementById('root'));
 *
 * Reference: https://react.dev/blog/2022/03/08/react-18-upgrade-guide
 */
import * as ReactDOM from 'react-dom';
import { createRoot, hydrateRoot } from 'react-dom/client';

// One React 18 root per container element. A WeakMap lets roots be garbage
// collected if the container element itself is removed and forgotten.
const rootsByContainer = new WeakMap();

/**
 * Drop-in replacement for ReactDOM.render. Reuses the existing root for a
 * container if one exists (an update), otherwise creates one.
 * @returns {import('react-dom/client').Root} the root backing the container
 */
function render(element, container) {
    if (!container) {
        console.error('ReactRenderHelper.render called with a null/undefined container.');
        return null;
    }

    let root = rootsByContainer.get(container);
    if (!root) {
        root = createRoot(container);
        rootsByContainer.set(container, root);
    }

    root.render(element);
    return root;
}

/**
 * Drop-in replacement for ReactDOM.unmountComponentAtNode.
 * @returns {boolean} true if a mounted root was found and unmounted
 */
function unmountComponentAtNode(container) {
    if (!container) {
        return false;
    }

    const root = rootsByContainer.get(container);
    if (!root) {
        return false;
    }

    root.unmount();
    rootsByContainer.delete(container);
    return true;
}

/**
 * Drop-in replacement for ReactDOM.hydrate (legacy SSR hydration).
 */
function hydrate(element, container) {
    if (!container) {
        console.error('ReactRenderHelper.hydrate called with a null/undefined container.');
        return null;
    }

    let root = rootsByContainer.get(container);
    if (!root) {
        root = hydrateRoot(container, element);
        rootsByContainer.set(container, root);
    } else {
        root.render(element);
    }
    return root;
}

export const ReactRenderHelper = {
    // Preserve everything react-dom still exports in v18: createPortal,
    // flushSync, findDOMNode, unstable_batchedUpdates, version, etc.
    ...ReactDOM,
    // New React 18 client APIs, exposed for callers that want them directly.
    createRoot,
    hydrateRoot,
    // Legacy-compatible overrides backed by createRoot.
    render,
    unmountComponentAtNode,
    hydrate,
};

export default ReactRenderHelper;
